import type { CommentView, ItemDetail, ItemLite } from "@plannello/shared";
import type { QueryClient } from "@tanstack/react-query";
import { queryClient } from "@/query-client";

/**
 * The single owner of item cache writes.
 *
 * Lite rows live per product under ["lites", productId] (an ItemLite[] in no particular order);
 * full details under ["item", ref] (ref = key or id as the route asked for it). Every write that
 * changes an item, whether it comes from an optimistic mutation, a server response, a 409 refresh
 * or (M4) an SSE event, goes through `applyItemUpsert` / `applyItemRemoval`, so the live sync can
 * replace the fetch-based loading later without touching any view.
 */

export const litesKey = (productId: string) => ["lites", productId] as const;
export const itemKey = (ref: string) => ["item", ref] as const;
export const searchKey = (q: string) => ["search", q] as const;

type LiteList = ItemLite[];

/**
 * Bookkeeping for live sync. `localWrites` remembers which items this tab wrote to recently, so
 * the server's echo of our own change is not announced as "updated by you". `removed` remembers
 * items we dropped (a local delete, or a remote delete event) so that an older upsert still on its
 * way cannot bring a deleted row back; a restore clears it.
 */
const LOCAL_ECHO_MS = 10_000;
const TOMBSTONE_MS = 60_000;
const localWrites = new Map<string, number>();
const removed = new Map<string, number>();
/** Key for "an item is being created": the new id is not known before the response arrives. */
export const CREATE_MARK = "*create";

export function markLocalWrite(id: string) {
  const now = Date.now();
  localWrites.set(id, now + LOCAL_ECHO_MS);
  if (localWrites.size > 500) for (const [k, until] of localWrites) if (until < now) localWrites.delete(k);
}

export function isLocalEcho(id: string, created = false): boolean {
  const now = Date.now();
  return (localWrites.get(id) ?? 0) > now || (created && (localWrites.get(CREATE_MARK) ?? 0) > now);
}

const isTombstoned = (id: string) => (removed.get(id) ?? 0) > Date.now();

function eachLiteList(qc: QueryClient, fn: (productId: string, list: LiteList) => void) {
  for (const [key, data] of qc.getQueriesData<LiteList>({ queryKey: ["lites"] })) {
    if (data) fn(String(key[1]), data);
  }
}

/** Finds a cached lite row by id across all loaded products. */
export function findLite(id: string, qc: QueryClient = queryClient): ItemLite | undefined {
  let found: ItemLite | undefined;
  eachLiteList(qc, (_p, list) => {
    found ??= list.find((i) => i.id === id);
  });
  return found;
}

export function findLiteByKey(key: string, qc: QueryClient = queryClient): ItemLite | undefined {
  const k = key.toUpperCase();
  let found: ItemLite | undefined;
  eachLiteList(qc, (_p, list) => {
    found ??= list.find((i) => i.key === k);
  });
  return found;
}

/** Merges fresh lite fields into any cached detail of the same item. */
function patchDetails(qc: QueryClient, lite: ItemLite) {
  qc.setQueriesData<ItemDetail>({ queryKey: ["item"] }, (old) =>
    old && old.id === lite.id ? { ...old, ...lite } : old,
  );
}

/**
 * Inserts or replaces an item. A row that changed product (move) is removed from its old list.
 * `replaceId` swaps an optimistic placeholder for the server row.
 */
export function applyItemUpsert(lite: ItemLite, opts: { replaceId?: string } = {}, qc: QueryClient = queryClient) {
  removed.delete(lite.id);
  const drop = new Set([lite.id, ...(opts.replaceId ? [opts.replaceId] : [])]);
  eachLiteList(qc, (productId, list) => {
    if (productId === lite.productId) return;
    if (list.some((i) => drop.has(i.id)))
      qc.setQueryData<LiteList>(litesKey(productId), (old) => old?.filter((i) => !drop.has(i.id)));
  });
  qc.setQueryData<LiteList>(litesKey(lite.productId), (old) => {
    if (!old) return old; // product not loaded: nothing to keep in sync
    let replaced = false;
    const next: LiteList = [];
    for (const i of old) {
      if (!drop.has(i.id)) next.push(i);
      else if (!replaced) {
        next.push(lite);
        replaced = true;
      }
    }
    if (!replaced) next.push(lite);
    return next;
  });
  patchDetails(qc, lite);
}

export function applyItemsUpsert(lites: ItemLite[], qc: QueryClient = queryClient) {
  for (const l of lites) applyItemUpsert(l, {}, qc);
}

/** Removes an item (deleted or no longer visible) from every list; details are marked deleted. */
export function applyItemRemoval(ref: { id: string }, qc: QueryClient = queryClient) {
  removed.set(ref.id, Date.now() + TOMBSTONE_MS);
  eachLiteList(qc, (productId, list) => {
    if (list.some((i) => i.id === ref.id))
      qc.setQueryData<LiteList>(litesKey(productId), (old) => old?.filter((i) => i.id !== ref.id));
  });
  qc.setQueriesData<ItemDetail>({ queryKey: ["item"] }, (old) =>
    old && old.id === ref.id ? { ...old, deletedAt: old.deletedAt ?? new Date().toISOString() } : old,
  );
}

export type RemoteOpts = {
  /** Accept a row with the same version as the cached one (relation events do not bump versions). */
  sameVersion?: boolean;
  /**
   * The event brings the item back (`item.restored`) or into view (`item.moved`, `item.upserted`),
   * so a recent local or remote removal must not block it. Plain updates are blocked: an older
   * update still on its way must not resurrect a row that was just deleted.
   */
  revive?: boolean;
};

/**
 * Applies an item row that arrived through the live stream. Idempotent: a row whose version is not
 * newer than the cached one is dropped, because lites loaded after the stream's head can already
 * contain later effects and events can be replayed after a reconnect. Returns true when a loaded
 * list or an open detail changed (the caller then flashes the row and announces the change).
 */
export function applyRemoteItem(lite: ItemLite, opts: RemoteOpts = {}, qc: QueryClient = queryClient): boolean {
  if (!opts.revive && isTombstoned(lite.id)) return false;
  const newer = (incoming: number, have: number) => (opts.sameVersion ? incoming >= have : incoming > have);
  const cached = findLite(lite.id, qc);
  const loaded = qc.getQueryData<LiteList>(litesKey(lite.productId)) !== undefined;
  let changed = false;

  // Open details (page or peek) first: patch the shared fields now, then refetch for activity and
  // children. This must run before the list write, which merges into details as well.
  let stale = false;
  qc.setQueriesData<ItemDetail>({ queryKey: ["item"] }, (old) => {
    if (!old || old.id !== lite.id || !newer(lite.version, old.version)) return old;
    stale = true;
    return { ...old, ...lite };
  });
  if (stale) {
    changed = true;
    refreshDetail(lite.id, qc);
  }
  if (cached ? newer(lite.version, cached.version) : loaded) {
    applyItemUpsert(lite, {}, qc);
    changed = true;
  }
  return changed;
}

/** A remote delete (or loss of access). Returns true when the item was known. */
export function applyRemoteRemoval(id: string, qc: QueryClient = queryClient): boolean {
  const known =
    !!findLite(id, qc) || qc.getQueriesData<ItemDetail>({ queryKey: ["item"] }).some(([, d]) => d?.id === id);
  applyItemRemoval({ id }, qc);
  return known;
}

/** Appends a comment to open details (page or peek) of its item, unless it is already there. */
export function applyRemoteComment(comment: CommentView, qc: QueryClient = queryClient): boolean {
  let added = false;
  qc.setQueriesData<ItemDetail>({ queryKey: ["item"] }, (old) => {
    if (!old || old.id !== comment.itemId || old.comments.some((c) => c.id === comment.id)) return old;
    added = true;
    return { ...old, comments: [...old.comments, comment] };
  });
  return added;
}

/** Refetches the details of an item (activity, children, relations) after a write. */
export function refreshDetail(id: string, qc: QueryClient = queryClient) {
  void qc.invalidateQueries({
    queryKey: ["item"],
    predicate: (q) => (q.state.data as ItemDetail | undefined)?.id === id,
  });
}

/** Stops in-flight lite fetches so they cannot overwrite an optimistic write. */
export async function cancelLiteFetches(productIds: Iterable<string>, qc: QueryClient = queryClient) {
  await Promise.all([...new Set(productIds)].map((p) => qc.cancelQueries({ queryKey: litesKey(p) })));
}
